<script setup lang="ts">
import {useDialogPluginComponent} from 'quasar'
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import DialogCloseIcon from "@/assets/editViewIcons/DialogCloseIcon.vue";
import {PropType, ref} from "vue";
import DialogHandler, {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import Translator from "@/utils/common/Translator.ts";
import DialogButtons from "@/utils/forms/DialogButtons.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import QuestionFieldsEditor from "@/components/questionFields/QuestionFieldsEditor.vue";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import NamaLabel from "@/components/NamaLabel.vue";
import ApprovalDecision from "@/utils/constants/ApprovalDecision.ts";

const {compactMode} = storeToRefs(useNamaFlagsStore());
const {dialogRef, onDialogHide, onDialogOK, onDialogCancel} = useDialogPluginComponent();
let anyActionClicked = ref(false);
const props = defineProps({
  handler: {type: Object as PropType<DialogHandler>, required: true}
})
let title = props.handler?.title;
let presenter: QuestionFieldsPresenter | undefined = props.handler?.presenter;
let actions: CommonDialogAction[] = props.handler?.actions ?? [];
let closeAction: Function | undefined = closeFn();
let singleColumnGroup: boolean | undefined = props.handler?.singleColumnGroup;

defineEmits([
  ...useDialogPluginComponent.emits
])

function closeFn(): Function | undefined {
  let cancelAction: Function | undefined = undefined;
  for (let action of actions) {
    if (ObjectChecker.isAnyEqualToFirst(action.id, DialogButtons.Cancel, DialogButtons.Close, DialogButtons.No))
      cancelAction = action.action;
  }
  return props.handler?.closeAction ?? cancelAction;
}

async function onCancelClick(action: Function | undefined) {
  anyActionClicked.value = true;
  document.body.focus();
  await useLoadingStore().awaitLoadingFinish(10);
  if (action)
    await action();
  onDialogCancel();
  anyActionClicked.value = false;
}

function getActionClass(actionId: string): string {
  if (actionId == ApprovalDecision.Approve)
    return 'tw-bg-green-600';
  if (actionId == ApprovalDecision.Reject)
    return 'tw-bg-red-600';
  if (actionId == ApprovalDecision.Return)
    return 'tw-bg-yellow-600';
  if (actionId == ApprovalDecision.ReturnToPreviousStep)
    return 'tw-bg-yellow-600';
  if (actionId == ApprovalDecision.ReturnToSpecificStep)
    return 'tw-bg-yellow-600';
  return 'tw-bg-primary-light dark:tw-bg-primary-dark';
}

async function onOkClick(action: Function | undefined, doNotCloseDialog: boolean) {
  anyActionClicked.value = true;
  document.body.focus();
  await useLoadingStore().awaitLoadingFinish(10);
  if (action)
    await action(presenter?.record.value);
  if (!doNotCloseDialog)
    onDialogOK();
  anyActionClicked.value = false;
}

</script>

<template>
  <q-dialog v-if="useLoginInfoStore().isLoggedIn || handler.alwaysAllowed" ref="dialogRef" @hide="onDialogHide" @keydown.esc="onCancelClick(closeAction)" persistent>
    <q-card class="tw-flex tw-flex-col tw-shadow-none dark:tw-bg-namaBackground-deep-dark tw-bg-namaBackground-deep-light !tw-rounded-none tw-w-full">
      <q-bar v-if="title" class="tw-flex-none tw-text-xs tw-text-namaFontColor-input-light tw-bg-[#CED4DA] dark:tw-bg-[#212529] dark:tw-text-white tw-h-10">
        <div v-text="title"></div>
        <q-space/>
        <q-btn dense flat @click="onCancelClick(closeAction)" :title="Translator.translateId( 'close')"
               class="nama-btn-hover nama-custom-btn focus:tw-bg-transparent">
          <q-avatar square class="tw-w-5">
            <DialogCloseIcon/>
          </q-avatar>
        </q-btn>
      </q-bar>
      <q-card-section class="tw-shrink tw-text-primary-light dark:tw-text-primary-dark tw-text-base tw-font-normal tw-h-fit tw-flex-nowrap tw-overflow-y-auto !tw-max-h-[90%]">
        <QuestionFieldsEditor :presenter="presenter" :singleColumnGroup="singleColumnGroup"  :doNotApplyThemeLayouts="true"/>
      </q-card-section>
      <q-card-section v-if="actions" class="tw-flex-none tw-p-1">
        <div class="tw-flex tw-gap-3 tw-w-full tw-px-0.5 tw-flex-wrap tw-justify-center">
          <template v-for="action of actions">
            <q-btn
                v-if="ObjectChecker.isAnyEqualToFirst(action.id, DialogButtons.Cancel, DialogButtons.Close, DialogButtons.No)"
                outline
                no-caps
                :label="Translator.translateId(action.id)"
                @click="onCancelClick(action.action)"
                :dense="compactMode"
                :disable="anyActionClicked"
                class="tw-text-namaGray dark:tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-min-w-16 tw-w-fit tw-max-w-fit"/>
            <q-btn
                v-else
                no-caps
                @click.stop="onOkClick(action.action, action.doNotCloseDialog)"
                @keydown.enter="onOkClick(action.action, action.doNotCloseDialog)"
                :dense="compactMode"
                :disable="anyActionClicked"
                class=" tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-min-w-fit tw-w-fit tw-max-w-fit"
                :class="getActionClass(action.id)">
              <NamaLabel :text="Translator.translateId(action.id)" class="tw-text-white">
                  <NamaIcon :allowed-value="action.id" :key="action.id" class="!tw-h-[17px] !tw-w-[17px] !tw-text-[17px] active-white"/>
              </NamaLabel>
            </q-btn>
          </template>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<style>

</style>
